import React, { useState, useEffect, useMemo, useCallback } from "react";
import { extent } from "d3-array";
import sortBy from "lodash.sortby";

import Localization from "../Localization";
import { formatAs } from "../settings.js";

import "./styles.scss";

import lightArrow from "../images/dropdown_arrow.svg";
import darkArrow from "../images/dark_arrow.svg";
import classNames from "classnames";
const allColumns = [
  "name",
  "technology",
  "dealtype",
  "capacity",
  "co2emissions",
  "year",
];
const allUnitColumns = ["unit", ...allColumns.slice(1)];
const formatter = {
  name: (v) =>
    Localization.getLanguage() === "zh"
      ? Localization.get(v.toUpperCase(), true) || v
      : v,
  technology: (v) => Localization.get(v),
  dealtype: (v) => Localization.get(v),
  capacity: (v) => formatAs.number(v),
  co2emissions: (v) => formatAs.number(v),
  year: (v) => v,
  unit: (v) => v,
};

function formatValue(value, column) {
  return formatter[column](value);
}

function columnClass(name, active) {
  return `column ${name} ${active === name ? "active" : ""}`;
}

const lockableCell = { cursor: "pointer" };
const lockedCell = { cursor: "pointer", color: "white" };
function getCellStyle(lockable, locked, background) {
  if (lockable) {
    if (locked) {
      return { ...lockedCell, background };
    }
    return lockableCell;
  }
  return {};
}

export default function Table(props) {
  const [activeProps, setActiveProps] = useState(props);
  useEffect(() => {
    const delay = props.delay ? 700 : 0;
    const delayHandle = setTimeout(() => setActiveProps(props), delay);
    return () => clearTimeout(delayHandle);
  }, [props]);

  const {
    data,
    groupKey,
    valueKey,
    setValueKey,
    isCapacity,
    geoSelection,
    selectedYears,
    allYears,
    lockable,
    lockedProject,
    updateLockedProject,
    setTooltipData,
    language,
  } = activeProps;

  const [hideProjects, setHideProjects] = useState({});
  const [hideGeos, setHideGeos] = useState({});

  const columns = useMemo(
    () => allColumns.filter((c) => c !== groupKey),
    [groupKey]
  );
  const unitColumns = useMemo(
    () => allUnitColumns.filter((c) => c !== groupKey),
    [groupKey]
  );
  const [openPlants, setOpenPlants] = useState([]);
  const [openPlantsByGeo, setOpenPlantsByGeo] = useState({});
  useEffect(() => {
    if (isCapacity && Object.keys(hideGeos).length) {
      setHideGeos({});
    }
  }, [isCapacity, hideGeos]);

  useEffect(() => {
    if (lockedProject) {
      setHideProjects((projects) => {
        const show = {};
        show[lockedProject.state] = false;
        return { ...projects, ...show };
      });
      setHideGeos((geos) => {
        const show = {};
        show[lockedProject[groupKey]] = false;
        return { ...geos, ...show };
      });
    }
  }, [lockedProject, groupKey]);

  const geos = geoSelection.join(", ");
  const geoLabel = geos === "" ? "Global" : geos;
  const localGeoLabel =
    Localization.getLanguage() === "zh"
      ? Localization.get(geoLabel.toUpperCase())
      : geoLabel;

  const [yearMin, yearMax] = extent(selectedYears);
  const yearRange =
    selectedYears.filter((y) => y !== "TBD").length ===
      allYears.filter((y) => y !== "TBD").length ||
    yearMax - yearMin === selectedYears.filter((y) => y !== "TBD").length - 1;
  const yearLabel = yearRange
    ? yearMin && yearMax
      ? `${yearMin}–${yearMax}`
      : ""
    : sortBy(selectedYears, (y) => (y === "TBD" ? 3000 : y)).join(", ");

  const [tableToggles, setTableToggles] = useState({
    expandActive: false,
    collapseActive: true,
    expandAll: () => {
      setHideGeos({});
      setHideProjects({});
    },
    collapseAll: () => {},
  });

  useEffect(() => {
    const groupNames = data.map((d) => d.name);
    const hiddenGroups = Object.keys(hideGeos).filter((k) => hideGeos[k]);
    const anyGroupsHidden =
      groupNames.some((n) => hiddenGroups.includes(n)) &&
      !groupNames.includes("capacity");
    const allGroupsHidden =
      groupNames.every((n) => hiddenGroups.includes(n)) ||
      groupNames.includes("capacity");

    const geoNames = [
      ...new Set(
        data
          .map((d) => d.geos.map((g) => g.name))
          .reduce((a, v) => a.concat(v), [])
      ),
    ];
    const hiddenGeos = Object.keys(hideProjects).filter((k) => hideProjects[k]);
    const anyGeosHidden = geoNames.some((n) => hiddenGeos.includes(n));
    const allGeosHidden = geoNames.every((n) => hiddenGeos.includes(n));

    const expandActive = anyGroupsHidden || anyGeosHidden;
    const collapseActive = !allGroupsHidden || !allGeosHidden;

    setTableToggles((tableToggles) => ({
      ...tableToggles,
      expandActive,
      collapseActive,
      collapseAll: () => {
        const nextHideGeos = {};
        groupNames.forEach((n) => (nextHideGeos[n] = true));
        setHideGeos(nextHideGeos);
        const nextHideProjects = {};
        geoNames.forEach((n) => (nextHideProjects[n] = true));
        setHideProjects(nextHideProjects);
      },
    }));
  }, [data, hideGeos, hideProjects]);

  const header = useMemo(
    () => ({
      name: (
        <div>
          <span className="large">{`${localGeoLabel}, `}</span>
          <span
            className={`${yearRange ? "large" : "small"}`}
          >{`${yearLabel}`}</span>
          <div className="toggles">
            <div
              className={`toggle ${tableToggles.expandActive ? "active" : ""}`}
              onClick={tableToggles.expandAll}
            >
              {Localization.get("Expand All")}
            </div>
            <div className="spacer"> | </div>
            <div
              className={`toggle ${
                tableToggles.collapseActive ? "active" : ""
              }`}
              onClick={tableToggles.collapseAll}
            >
              {Localization.get("Collapse All")}
            </div>
          </div>
        </div>
      ),
      technology: Localization.get("Technology"),
      dealtype: Localization.get("Deal Type"),
      capacity: Localization.get("Capacity (MW)"),
      co2emissions: (
        <span style={{ position: "relative" }}>
          {Localization.get("Annual")} <span>CO</span>
          <sub>2</sub>
          <br />{" "}
          <span className="nudgeRight">
            {Localization.get("Emissions")} ({Localization.get("K Ton")})
          </span>
        </span>
      ),
      year: Localization.get("Commission Year"),
    }),
    [localGeoLabel, yearLabel, yearRange, tableToggles]
  );

  const hoverProject = useMemo(
    () =>
      function (event, project) {
        if (project) {
          let x = 0;
          let y = 0;
          if ("pageX" in event) {
            x = event.pageX;
            y = event.pageY;
          }
          setTooltipData({
            data: { projects: [project] },
            type: "project",
            lockable,
            x,
            y,
            table: true,
          });
        } else {
          setTooltipData(null);
        }
      },
    [lockable, setTooltipData]
  );

  const tableHeader = useMemo(
    () => (
      <div className="row header">
        {columns.map((c) => (
          <div
            key={c}
            className={columnClass(c, valueKey)}
            style={{ cursor: setValueKey && c !== "name" ? "pointer" : "auto" }}
            onClick={() =>
              setValueKey && c !== "name" ? setValueKey(c) : null
            }
          >
            <div className="cell">{header[c]}</div>
          </div>
        ))}
      </div>
    ),
    [columns, header, valueKey, setValueKey]
  );

  const collapseAllPlants = useCallback(
    (geo) => (event) => {
      const plantsToToggle = geo.projects.map((project) => {
        const plantId = project.plantID;
        const unitId = project.unit.replace(/ /g, "-");
        const plantIdWithOutUnitId = plantId.replace(unitId, "");
        return plantIdWithOutUnitId;
      });
      // setOpenPlants(openPlants => {
      //   const nextOpenPlants = [...openPlants].filter(p => !plantsToToggle.includes(p));
      //   return nextOpenPlants;
      // })
      // console.log(geo)
      setOpenPlantsByGeo((openPlantsByGeo) => {
        const nextOpenPlantsByGeo = { ...openPlantsByGeo };
        nextOpenPlantsByGeo[geo.name] = (
          nextOpenPlantsByGeo[geo.name] ? nextOpenPlantsByGeo[geo.name] : []
        ).filter((p) => !plantsToToggle.includes(p));
        return nextOpenPlantsByGeo;
      });
      event.preventDefault();
      event.stopPropagation();
      return false;
    },
    []
  );

  const expandAllPlants = useCallback(
    (geo) => (event) => {
      const plantsToToggle = geo.projects.map((project) => {
        const plantId = project.plantID;
        const unitId = project.unit.replace(/ /g, "-");
        const plantIdWithOutUnitId = plantId.replace(unitId, "");
        return plantIdWithOutUnitId;
      });
      // setOpenPlants(openPlants => {
      //   const nextOpenPlants = Array.from(new Set([...openPlants].concat(plantsToToggle)));
      //   return nextOpenPlants;
      // })
      setOpenPlantsByGeo((openPlantsByGeo) => {
        const nextOpenPlantsByGeo = { ...openPlantsByGeo };
        nextOpenPlantsByGeo[geo.name] = Array.from(
          new Set(
            [
              ...(nextOpenPlantsByGeo[geo.name]
                ? nextOpenPlantsByGeo[geo.name]
                : []),
            ].concat(plantsToToggle)
          )
        );
        return nextOpenPlantsByGeo;
      });
      event.preventDefault();
      event.stopPropagation();
      return false;
    },
    []
  );
  // console.log(openPlantsByGeo)

  const tableContent = useMemo(
    () =>
      data.map((group) => (
        <div
          key={group.name}
          className={`section ${isCapacity ? "capacity" : "group"}`}
          style={{ background: group.groupFill }}
        >
          <div className="row group" style={{ background: group.fill }}>
            {isCapacity
              ? null
              : columns.map((c) => (
                  <div
                    key={`${group.name}-${c}`}
                    className={columnClass(c, valueKey)}
                    onClick={() => {
                      if (c === "name") {
                        const nextHideGeos = Object.assign({}, hideGeos);
                        nextHideGeos[group.name] = hideGeos[group.name]
                          ? false
                          : true;
                        setHideGeos(nextHideGeos);
                      }
                    }}
                  >
                    <div className="cell">
                      <span>
                        {c === "year"
                          ? null
                          : formatValue(
                              c === "capacity" || c === "co2emissions"
                                ? Number(group[c])
                                : group[c],
                              c
                            )}
                      </span>
                      {c === "name" ? (
                        <div
                          className={`arrow ${
                            hideGeos[group.name] ? "rotated" : ""
                          }`}
                        >
                          <img src={lightArrow} alt="V" />
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                ))}
          </div>
          <div
            className={`section geo ${hideGeos[group.name] ? "collapse" : ""}`}
          >
            {group.geos.map((geo) => {
              // console.log(geo)
              // console.log(openPlants)
              const countPlantsExpanded = geo.projects.filter((project) => {
                const plantId = project.plantID;
                const unitId = project.unit.replace(/ /g, "-");
                const plantIdWithOutUnitId = plantId.replace(unitId, "");
                const show = (
                  openPlantsByGeo[geo.name] ? openPlantsByGeo[geo.name] : []
                ).includes(plantIdWithOutUnitId);
                return show;
              });

              // (countPlantsExpanded)
              const highlightCollapseAll = countPlantsExpanded.length === 0;
              const highlightExpandAll =
                countPlantsExpanded.length === geo.projects.length;
              return (
                <div key={geo.name} className="section geo">
                  <div
                    className="row geo"
                    style={{ background: isCapacity ? "#CCCCCC" : null }}
                  >
                    {columns.map((c) => (
                      <div
                        key={`${geo.name}-${c}`}
                        className={columnClass(c, valueKey)}
                        style={{
                          background: geo.fill,
                          color: geo.fill ? "white" : null,
                        }}
                        onClick={() => {
                          if (c === "name") {
                            const nextHideProjects = Object.assign(
                              {},
                              hideProjects
                            );
                            nextHideProjects[geo.name] = hideProjects[geo.name]
                              ? false
                              : true;
                            setHideProjects(nextHideProjects);
                          }
                        }}
                      >
                        <div className="cell">
                          <span>
                            {c === "year"
                              ? null
                              : formatValue(
                                  c === "capacity" || c === "co2emissions"
                                    ? Number(geo[c])
                                    : geo[c],
                                  c
                                )}
                            {c === "name" ? (
                              <div
                                style={{
                                  display: "inline-block",
                                  marginLeft: "1em",
                                  fontSize: "0.8em",
                                }}
                              >
                                <div
                                  className={classNames("toggle", {
                                    active: !highlightExpandAll,
                                  })}
                                  onClick={expandAllPlants(geo)}
                                >
                                  {Localization.get("Expand All")}{" "}
                                  {Localization.get("Plants")}
                                </div>
                                <div className="spacer"> | </div>
                                <div
                                  className={classNames("toggle", {
                                    active: !highlightCollapseAll,
                                  })}
                                  onClick={collapseAllPlants(geo)}
                                >
                                  {Localization.get("Collapse All")}{" "}
                                  {Localization.get("Plants")}
                                </div>
                              </div>
                            ) : null}
                          </span>
                          {c === "name" ? (
                            <div
                              className={`arrow ${
                                hideProjects[geo.name] ? "rotated" : ""
                              }`}
                            >
                              <img
                                src={geo.fill ? lightArrow : darkArrow}
                                alt="V"
                              />
                            </div>
                          ) : (
                            ""
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    className={`section project ${
                      hideProjects[geo.name] ? "collapse" : ""
                    }`}
                  >
                    {geo.projects.map((project, projectIndex) => {
                      const plantId = project.plantID;
                      const unitId = project.unit.replace(/ /g, "-");
                      const plantIdWithOutUnitId = plantId.replace(unitId, "");
                      // const show = openPlants.includes(plantIdWithOutUnitId)
                      const show = openPlantsByGeo[geo.name]
                        ? openPlantsByGeo[geo.name].includes(
                            plantIdWithOutUnitId
                          )
                        : false;
                      // console.log(geo.name)
                      // console.log(openPlantsByGeo[geo.name])
                      // console.log(project.units)
                      // console.log(show)
                      // console.log(groupKey)
                      if (show) {
                        // debugger
                      }
                      const toggleProject = (event) => {
                        if (show) {
                          // setOpenPlants(openPlants.filter(p => p !== plantIdWithOutUnitId))
                          setOpenPlantsByGeo((openPlantsByGeo) => {
                            const nextOpenPlants = [
                              ...(openPlantsByGeo[geo.name]
                                ? openPlantsByGeo[geo.name]
                                : []),
                            ].filter((p) => p !== plantIdWithOutUnitId);
                            return {
                              ...openPlantsByGeo,
                              [geo.name]: nextOpenPlants,
                            };
                          });
                        } else {
                          // setOpenPlants([...openPlants, plantIdWithOutUnitId])
                          setOpenPlantsByGeo((openPlantsByGeo) => {
                            const nextOpenPlants = Array.from(
                              new Set(
                                [
                                  ...(openPlantsByGeo[geo.name]
                                    ? openPlantsByGeo[geo.name]
                                    : []),
                                ].concat(plantIdWithOutUnitId)
                              )
                            );
                            return {
                              ...openPlantsByGeo,
                              [geo.name]: nextOpenPlants,
                            };
                          });
                        }
                        event.preventDefault();
                        event.stopPropagation();
                        return false;
                      };
                      const renderProject = (project, cols, hasSubUnits) => {
                        const isPlant = project.units;

                        const locked =
                          lockedProject && isPlant
                            ? lockedProject.plantID === project.plantID
                            : false;
                        const cellStyle = getCellStyle(
                          lockable,
                          locked,
                          group.fill
                        );
                        const extraValues = {};
                        if (isPlant) {
                          // debugger
                          // project.co2emissions = project.units.filter(d => d[groupKey] === group.name).reduce((acc, unit) => acc + unit.co2emissions, 0)
                          // project.capacity = project.units.filter(d => d[groupKey] === group.name).reduce((acc, unit) => acc + unit.capacity, 0)
                          // console.log(project.units.filter(d => d[groupKey] === group.name).reduce((acc, unit) => acc + unit.co2emissions, 0))
                          extraValues.co2emissions =
                            project.units && project.units.length
                              ? project.units
                                  .filter((d) => d[groupKey] === group.name)
                                  .reduce(
                                    (acc, unit) =>
                                      Number(acc) + Number(unit.co2emissions),
                                    0
                                  )
                              : 0;
                          extraValues.capacity =
                            project.units && project.units.length
                              ? project.units
                                  .filter((d) => d[groupKey] === group.name)
                                  .reduce(
                                    (acc, unit) =>
                                      Number(acc) + Number(unit.capacity),
                                    0
                                  )
                              : 0;
                        }
                        return (
                          <div
                            key={project.plantID + project.unit}
                            ref={locked ? props.lockedRowRef : null}
                            className={`row project ${locked ? "locked" : ""}`}
                            data-group={group.name}
                            data-geo={geo.name}
                            data-project={project.plantID}
                            onClick={
                              lockable
                                ? () =>
                                    updateLockedProject(project, true, false)
                                : null
                            }
                            style={{ pointerEvents: isPlant ? "" : "none" }}
                          >
                            {cols.map((c, i) => (
                              <div
                                key={`${project.plantID}-${c}`}
                                className={columnClass(c, valueKey)}
                                data-group={group.name}
                                data-geo={geo.name}
                                data-project={project.plantID}
                              >
                                <div
                                  className="cell"
                                  style={cellStyle}
                                  data-group={group.name}
                                  data-geo={geo.name}
                                  data-project={project.plantID}
                                >
                                  {i === 0 ? (
                                    <img
                                      style={{
                                        visibility: hasSubUnits
                                          ? "visible"
                                          : "hidden",
                                      }}
                                      src={darkArrow}
                                      onClick={toggleProject}
                                      alt=""
                                      className={`plantToggle ${
                                        show ? "show" : ""
                                      }`}
                                    />
                                  ) : null}
                                  {formatValue(
                                    c === "capacity" || c === "co2emissions"
                                      ? Number(
                                          extraValues[c] || project[c] || 0
                                        )
                                      : extraValues[c] || project[c],
                                    c
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      };
                      const hasUnits =
                        project.units && project.units.length > 1;
                      return (
                        <React.Fragment key={projectIndex}>
                          {renderProject(project, columns, hasUnits)}
                          {hasUnits &&
                            show &&
                            project.units
                              .sort((a, b) =>
                                `${b[valueKey]}`.localeCompare(
                                  `${a[valueKey]}`,
                                  "en",
                                  { numeric: true }
                                )
                              )
                              .filter(
                                (d) =>
                                  groupKey === "countryID" ||
                                  d[groupKey] === group.name
                              )
                              .map((unit) => {
                                return renderProject(unit, unitColumns, false);
                              })}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )),
    [
      columns,
      data,
      hideGeos,
      hideProjects,
      isCapacity,
      lockable,
      lockedProject,
      updateLockedProject,
      valueKey,
      props.lockedRowRef,
      language,
      openPlants,
      setOpenPlants,
      unitColumns,
      openPlantsByGeo,
      setOpenPlantsByGeo,
    ]
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const hoverTable = useMemo(
    () =>
      function (event) {
        const { target } = event;
        const { group, geo, project } = target.dataset;
        if (group && geo && project) {
          const investment = data
            .find((g) => g.name === group)
            .geos.find((g) => g.name === geo)
            .projects.find((p) => p.plantID === project);
          hoverProject(event, investment);
        } else {
          hoverProject(null, null);
        }
      },
    [data, hoverProject]
  );

  // Debug logging for the table data to help diagnose capacity calculation issues
  useEffect(() => {
    if (
      data &&
      data.length > 0 &&
      selectedYears &&
      selectedYears.length === 1
    ) {
      console.log(`DEBUG Table Component - Data for ${selectedYears[0]}:`);

      // Calculate the total capacity to verify it matches summary
      let totalTableCapacity = 0;
      data.forEach((group) => {
        totalTableCapacity += Number(group.capacity || 0);

        // Check for specific technologies
        if (group.name === "Coal" || group.name === "Gas") {
          console.log(
            `Table received ${group.name}: capacity=${group.capacity}, value=${
              group.value || group[valueKey]
            }`
          );

          // Validate if the geo totals match the group total
          if (group.geos && group.geos.length > 0) {
            let geoTotal = 0;
            group.geos.forEach((geo) => {
              geoTotal += Number(geo.capacity || 0);
            });
            console.log(
              `${group.name} geo sum: ${geoTotal}, group capacity: ${
                group.capacity
              }, difference: ${Math.abs(geoTotal - group.capacity)}`
            );

            // If there's a significant difference, log a warning
            if (Math.abs(geoTotal - group.capacity) > 0.1) {
              console.warn(
                `INCONSISTENCY: ${group.name} geo total doesn't match group capacity`
              );
            }
          }
        }
      });

      console.log(`Total Table capacity: ${totalTableCapacity}`);
    }
  }, [data, selectedYears, valueKey, groupKey]);

  return (
    <div className="Table">
      <div
        className="table"
        onMouseMove={(event) => hoverTable(event)}
        onMouseOut={() => hoverProject(null, null)}
      >
        {tableHeader}
        {tableContent}
      </div>
    </div>
  );
}
